import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';
import { useData } from '../../contexts/DataContext';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';

export default function TeamManagement() {
  const { teams, addTeam, updateTeam, deleteTeam } = useData();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [newTeam, setNewTeam] = useState({
    name: '',
    members: []
  });

  const handleOpenDialog = (team = null) => {
    if (team) {
      setEditingTeam(team);
      setNewTeam({
        name: team.name,
        members: team.members || []
      });
    } else {
      setEditingTeam(null);
      setNewTeam({
        name: '',
        members: []
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTeam(null);
    setNewTeam({
      name: '',
      members: []
    });
  };

  const handleSaveTeam = () => {
    if (!newTeam.name.trim()) return;

    if (editingTeam) {
      updateTeam(editingTeam.id, {
        ...editingTeam,
        ...newTeam,
        updatedAt: new Date().toISOString()
      });
    } else {
      addTeam({
        ...newTeam,
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    }

    handleCloseDialog();
  };

  const handleDeleteTeam = (teamId) => {
    if (window.confirm('Are you sure you want to delete this team?')) {
      deleteTeam(teamId);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Teams</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Team
        </Button>
      </Box>

      <List>
        {teams?.map((team) => (
          <Paper key={team.id} sx={{ mb: 2 }}>
            <ListItem>
              <ListItemText
                primary={team.name}
                secondary={
                  <Box sx={{ mt: 1 }}>
                    {team.members?.map((member, index) => (
                      <Chip
                        key={index}
                        label={member}
                        size="small"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  aria-label="edit"
                  onClick={() => handleOpenDialog(team)}
                  sx={{ mr: 1 }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={() => handleDeleteTeam(team.id)}
                >
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          </Paper>
        ))}
      </List>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTeam ? 'Edit Team' : 'Add New Team'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Team Name"
              value={newTeam.name}
              onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Team Members"
              value={newTeam.members.join(', ')}
              onChange={(e) => setNewTeam({ ...newTeam, members: e.target.value.split(',').map(m => m.trim()).filter(m => m) })}
              fullWidth
              helperText="Enter team member names separated by commas"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSaveTeam}
            variant="contained"
            disabled={!newTeam.name.trim()}
          >
            {editingTeam ? 'Save Changes' : 'Add Team'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 